import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from "@angular/core";
import { TypePercent } from "../../enums/enums/typePercent.enum";
import { EurDiffComponent } from "../eur-diff/eur-diff.component";
import { ChipsComponent } from "app/shared/modules/chips/chips.component";
import { ChipState } from "../../enums/chip-state.enum";

@Component({
  selector: "vl-percent",
  standalone: true,
  imports: [EurDiffComponent, ChipsComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="percent-container flex-container gap-4">
      @if (hasPercent()) {
        <vl-chips
          [state]="chipState()"
          size="small"
          cursor="default"
          style="width: fit-content"
        >
          {{ showPlus() ? "+" : "" }}{{ formatted() }}%
        </vl-chips>
      }

      @if (valeur() !== null && valeur() !== undefined) {
        <vl-eur-diff [v]="valeur()!" [valeurUnite]="valeurUnite()" />
      }
    </div>
  `,
  styles: [
    `
      .percent-container {
        display: inline-flex;
        width: fit-content;
      }
    `,
  ],
})
export class PercentComponent {
  // Inputs
  percent = input<number | null>(null);
  typePercent = input<TypePercent | null>(null);
  zeroize = input<boolean>(false);
  fixed = input<number | null>(null);
  valeur = input<number | null>(null);
  simplify = input<boolean>(false);
  valeurUnite = input<string | null>(null);
  percentNormal = input<number | null>(null);
  percentMax = input<number | null>(null);
  noPlus = input<boolean>(false);

  // Signaux dérivés
  readonly hasPercent = computed(
    () =>
      this.percent() !== null &&
      this.percent() !== undefined &&
      !Number.isNaN(this.percent()) &&
      Number.isFinite(this.percent()),
  );

  private readonly valueToShow = computed<number | null>(() => {
    const p = this.percent();
    if (p === null || p === undefined) return null;
    return this.zeroize() ? p - 100 : p;
  });

  readonly formatted = computed<string>(() => {
    const v = this.valueToShow();
    if (v === null) return "";
    const f = this.fixed();
    return f !== null ? v.toFixed(f) : String(v);
  });

  readonly showPlus = computed<boolean>(() => {
    const v = this.valueToShow();
    if (v === null || this.noPlus()) return false;
    if (this.zeroize()) return this.simplify() && v > 0;
    return v > 0;
  });

  // Mapping vers les états de vl-chips
  readonly chipState = computed<ChipState>(() => {
    const t = this.typePercent();
    const p = this.percent();
    switch (t) {
      case TypePercent.OF_TOTAL:
        return this.getPercentTotalState(p);
      case TypePercent.OF_PERSONNEL:
        return this.getPercentPersonnelState(p);
      case TypePercent.OF_MATIERE:
        return this.getPercentState(p);
      default:
        return "accent"; // accent = violet, bien visible
    }
  });

  // Méthodes de calcul d'état
  private getPercentState(score: number | null | undefined): ChipState {
    if (score === null || score === undefined) return "accent";

    const comp = this.zeroize() ? score - 100 : score;

    let norm = this.percentNormal();
    let max = this.percentMax();

    if (norm == null && max != null) norm = max / 2;
    if (max == null && norm != null) max = norm * 2;
    if (norm == null && max == null) {
      norm = 3;
      max = 10;
    }

    if (comp < -max!) return "accent";
    if (comp <= norm!) return "valid";
    if (comp <= max!) return "warn";
    if (comp <= 2 * max!) return "invalid";
    return "accent";
  }

  private getPercentTotalState(score: number | null | undefined): ChipState {
    if (score === null || score === undefined) return "accent";
    if (score < 70) return "accent";
    if (score < 80) return "invalid";
    if (score < 90) return "warn";
    return "valid";
  }

  private getPercentPersonnelState(
    score: number | null | undefined,
  ): ChipState {
    if (score === null || score === undefined) return "accent";
    if (score < -30) return "accent";
    if (score <= 10) return "valid";
    if (score <= 20) return "warn";
    return "invalid";
  }
}
